export const industryHeadings = {
    retail: 'Retail Stores',
    health: 'Healthcare',
    construction: 'Construction',
    realEstate: 'Real Estate',
    clothing: 'Clothing Shops',
    startups: 'Startups',
} as const;

export type IndustryKey = keyof typeof industryHeadings;

interface LogoExample {
    src: string;
    altText: string;
}

export type LogoExamples = {
    [Key in IndustryKey]: LogoExample[];
};

const cloudinaryUrl =
    'https://cms.cloudinary.vpsvc.com/image/upload/c_scale,dpr_auto,f_auto,w_auto';
const folderPath = '/vista-logos-pages/en-US/logomaker-gen-ai/logo-examples';

export const logoExamples: LogoExamples = {
    retail: [
        {
            src: `${cloudinaryUrl}${folderPath}/retail/market.webp`,
            altText:
                'Logo for "The Local Market" featuring a woven basket filled with vegetables with tagline "Fresh product and local products".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/kids.webp`,
            altText: `Colorful logo of "Kids' Kingdom" featuring playful typography with tagline "Designed for children".`,
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/crafty.webp`,
            altText:
                'Logo of "Crafty Creations" featuring a hand holding an artists palette with tagline "DIY kits and classes for budding artists".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/chic.webp`,
            altText:
                'Logo for "Chic Boutique" featuring a handbag and feather in pink tones with tagline "Accessories and Beauty Products".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/green.webp`,
            altText:
                'Logo for "The Green Corner", featuring a stylised shopping cart sprouting green leaves with tagline "Organic Products & Sustainable Goods".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/pets.webp`,
            altText:
                'Logo design for "Pet Paradise," featuring an illustration of a dog and a cat with tagline "Everything your fury friends need".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/homey.webp`,
            altText:
                '"Home Haven" logo featuring a stylized lamp and warm colors with tagline "Enhance your living space".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/bargain.webp`,
            altText:
                'Logo for "Bargain Boutique" featuring a full shopping basket with tagline "Affordable Essentials".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/gourmet.webp`,
            altText:
                'Logo design for "Gourmet Goodies" featuring a chefs hat and cutlery with tagline "Specialty foods, snacks and kitchen gadgets".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/seasonal.webp`,
            altText:
                'Logo of "Seasonal Splendor" featuring floral elements in a circular motif with tagline "Celebrating every season".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/threads.webp`,
            altText:
                '"Trendy Threads Boutique" logo showcasing a stylized hat and dress with tagline "Chic apparel for all ages".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/retail/gadget.webp`,
            altText:
                '"The Gadget Garage" logo featuring blue and yellow lightning bolts with tagline "Innovative gadgets and tech accessories.',
        },
    ],
    health: [
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/carefirst.webp`,
            altText:
                '"CareFirst Clinic" logo featuring a pink heart and stethoscope above the tagline "Family-friendly medical services".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/wellspring.webp`,
            altText:
                '"Wellspring Health" logo featuring leafy plant elements in a circular motif with tagline "Preventive care and holistic health".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/brightpath.webp`,
            altText:
                'Logo of "BrightPath Health Center" featuring shining sun rays above the tagline "A modern clinic".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/thrive.webp`,
            altText:
                '"Thrive Health Network" logo featuring a medical cross in a crest with tagline below "Healthy Lifestyles and Disease Prevention".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/harmony.webp`,
            altText:
                '"Harmony Health Partners" logo featuring two hands shaking in a heart shape above the tagline "Teamwork and Communication".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/healing.webp`,
            altText:
                '"Healing Hearts Medical Group" logo featuring a heart symbol inside a ribbon with the tagline "Compassionate medical practice".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/happyteeth.webp`,
            altText:
                '"Happy Teeth Dentistry" logo featuring a sparkling smiling tooth with tagline "Crafting healthy smiles".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/pathway.webp`,
            altText:
                '"Pathway Physiotherapy" logo featuring a road leading to a heart with tagline "Compassionate Care".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/chirokids.webp`,
            altText:
                '"Chirocare for Kids" logo, featuring a spine and tagline "Gentle chiropractic care for children".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/spinemind.webp`,
            altText:
                '"Spine & Mind Chiropractic" logo featuring a line drawing of a brain and tagline "Connecting mind and body".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/toothfairy.webp`,
            altText:
                'Logo of "The Tooth Fairy Clinic" featuring a flying tooth with a wand above the tagline "Making dental care fun".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/healthcare/nurture.webp`,
            altText:
                'Logo for "Nurture Osteopathy" with open hands holding a heart and the tagline "Gentle osteopathic care".',
        },
    ],
    construction: [
        {
            src: `${cloudinaryUrl}${folderPath}/construction/dream.webp`,
            altText:
                'Logo of "Dream Builders Co", featuring a crane over a frame structure with the tagline "Expert Construction and Design Services".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/eco.webp`,
            altText:
                '"Eco-friendly builders" logo, with an illustration of a home with solar panels and rooftop garden with "Sustainable and environmentally responsible".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/hammer.webp`,
            altText:
                '"Reliable Renovations" logo showing two hammers over a window with the tagline "Home renovation and remodeling".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/skyhigh.webp`,
            altText:
                'Logo of "Skyhigh Construction" featuring a skyline of building skyscrapers and tagline "Reaching new heights".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/nextgen.webp`,
            altText:
                '"NextGen Builders" logo featuring a house appearing like a circuit board above the tagline "Pioneering cutting-edge technology".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/heritage.webp`,
            altText:
                '"Heritage Homes Construction" logo showcasing a classical portico entrance and tagline "Reviving classic designs".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/bricks.webp`,
            altText:
                'Logo for "Precision Builders" featuring a stack of bricks and tagline "Detail-oriented construction services".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/community.webp`,
            altText:
                'Logo of "Community Builders Co", featuring building plans with a growing tree and the tagline "Crafting spaces that foster connection and growth".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/family.webp`,
            altText:
                'Logo of "Family First Builders" featuring a tree inside an outline of a house and the tagline "Where families thrive".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/foundations.webp`,
            altText:
                'Logo for "Solid Foundations Construction" with tagline "Durable and reliable structures".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/craft.webp`,
            altText:
                'Logo for "Quality Craft Construction" featuring a wooden box with a saw above the tagline "Delivering exceptional craftsmanship".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/construction/visionary.webp`,
            altText:
                'Logo of "Visionary Structures" featuring a stylized eye and the tagline "Transforming visions".',
        },
    ],
    realEstate: [
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/keystone.webp`,
            altText:
                'Logo of "Keystone Realty Group" featuring a geometric house inside a shield with the tagline "Expert Real Estate Services".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/bluesky.webp`,
            altText:
                '"Blue Sky Realty" logo featuring a house floating in clouds above the tagline "Providing clear pathways to homeownership".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/home_haven.webp`,
            altText:
                '"Home Haven Realty" logo featuring a house with a family holding hands and the tagline "A Family-Oriented Real Estate Agency".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/safe_harbor.webp`,
            altText:
                'Logo for "Safe Harbor Properties" featuring an anchor, waves and outline of a house with the tagline "Navigating the real estate waters".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/chapter.webp`,
            altText:
                '"Next Chapter Realty" logo featuring a house key with the tagline "Turning the page to your next home".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/door.webp`,
            altText:
                'Logo of "Open Door Realty", featuring a home inside an arch design with the tagline "A seamless home buying experience".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/cornerstone.webp`,
            altText:
                'Logo of "Cornerstone Homes" featuring rolled up plans and the tagline "Building strong foundations for families ".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/nest.webp`,
            altText:
                'Logo of "Nest and Nook Properties" featuring a birds nest under a house roof and tagline "We help you find your nest".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/hometown.webp`,
            altText:
                '"Home Town Realty Group" logo featuring a house key stylized like a map pin and the tagline "Your local experts in real estate".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/heart.webp`,
            altText:
                'Logo of "Heart of Home Realty" featuring a trail of hearts flowing from a house and the tagline "We connect you with the right property".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/welcome.webp`,
            altText:
                'Logo of "Welcome Home Realty" featuring two hands shaking inside the outline of a house above the tagline "a personal touch and expert advice".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/real-estate/sunny.webp`,
            altText:
                '"Sunny Side Realty" logo featuring a house roof emitting sun rays and the tagline "We specialize in bright, welcoming homes".',
        },
    ],
    clothing: [
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/bright.webp`,
            altText:
                'Logo for "Bright & Breezy Wear" featuring bold yellow text with the tagline "Summer days and beach vacations".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/atheleisure.webp`,
            altText:
                'Logo of "Athletise Haven" with an illustration of a green and orange sneaker above the tagline "Workouts and Casual Wear".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/fashionistas.webp`,
            altText: `Logo of "Little Fashionistas" featuring a mannequin of a girl and the tagline "delightful children's clothing".`,
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/cozy.webp`,
            altText:
                '"Cozy closet" logo featuring clothes laid out to form the letter C and the tagline "Casual clothing for families".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/urban.webp`,
            altText:
                'Logo for "Urban Style Co", featuring bold black text on a grey background with the tagline "Modern Clothing Brand".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/sunny.webp`,
            altText:
                'Logo for "Sunny Day Dresses" featuring a clothes hanger with a yellow sun above the tagline "Perfect for any sunny occasion".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/threads.webp`,
            altText:
                'Logo of "Trendy Threads Boutique", featuring typography consisting of clothing articles and the tagline "Boutique Fashion".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/formal.webp`,
            altText:
                '"Formal Finery" logo featuring cursive lettering below a flowing evening gown with the tagline "Elegant attire for special occasions".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/activewear.webp`,
            altText:
                'Logo for "Activewear Avenue" featuring green and orange text on a grey background with the tagline "Activewear for fitness enthusiasts".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/dudes.webp`,
            altText:
                'Logo for "Dapper Dudes", featuring cufflinks and the tagline "Accessories for the modern gentleman".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/corner.webp`,
            altText:
                'Logo of "Casual Corner", featuring an illustration of a couch and the tagline "Comfortable, everyday wear".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/clothing/vintage.webp`,
            altText:
                '"Vintage Vibes Clothing" logo featuring a polaroid of a dress and the tagline "Curated Retro Fashion".',
        },
    ],
    startups: [
        {
            src: `${cloudinaryUrl}${folderPath}/startups/launchpad.webp`,
            altText:
                '"Launchpad Innovations" logo featuring a lightbulb designed to look like a rocket with the tagline "A vibrant hub for entrepreneurs".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/sprout.webp`,
            altText:
                'Logo for "Sprout startup" featuring a green growing plant with an arrow pointing out of it with the tagline "Essential tools for growth".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/igniter.webp`,
            altText:
                '"Idea Igniters" logo featuring an arrow inside a red flame with the tagline "Helping startups build their brand".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/kickstart.webp`,
            altText:
                '"Kickstart Collective" logo featuring a rocket and blue stars with the tagline "Coaching and resources for startups".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/aspire.webp`,
            altText: '"Aspire Ventures" logo featuring a lightbulb taking off like a rocket.',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/startupsyn.webp`,
            altText:
                'Logo of "Startup Synergy", featuring a network diagram with the tagline "Bringing together entrepreneurs and investors".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/innovate.webp`,
            altText:
                'Logo of "Innovate and Elevate" featuring a desk with leaves sprouting from it with the tagline "Accelerating the Growth".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/dreambig.webp`,
            altText:
                '"Dream Big Startups" logo featuring a tree at the center and the tagline "Networking events and funding opportunities".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/creative.webp`,
            altText:
                'Logo of "Creative Catalyst" featuring a network of gold stars and the tagline "Networking events and funding opportunities".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/bright.webp`,
            altText:
                'Logo of "Bright Beginnings" featuring a chart trending upwards and a yellow sun with the tagline "Business Consultancy".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/incubator.webp`,
            altText:
                'Logo design for "Idea incubator" featuring a light bulb with a baby chick inside and the tagline "Turn ideas into viable businesses".',
        },
        {
            src: `${cloudinaryUrl}${folderPath}/startups/launchlane.webp`,
            altText:
                'Logo for "Launch Lane" featuring a clock with red arrows around the outside and the tagline "A fast-track program".',
        },
    ],
};
